import { BaseEntity, MediumType, Medium } from '@ianesthesie-kit/services';

export enum QuestionType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  CHOICE = 'CHOICE',
  BI_CHOICE = 'BI_CHOICE',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  UPLOAD = 'UPLOAD',
  RATING = 'RATING',
  STATEMENT = 'STATEMENT'
}

export enum ConvertibleUnit {
  METRIC_WEIGHT = 'METRIC_WEIGHT',
  METRIC_HEIGHT = 'METRIC_HEIGHT',
  IMPERIAL_WEIGHT = 'IMPERIAL_WEIGHT',
  IMPERIAL_HEIGHT = 'IMPERIAL_HEIGHT',
  METRIC_TEMPERATURE = 'METRIC_TEMPERATURE',
  IMPERIAL_TEMPERATURE = 'IMPERIAL_TEMPERATURE'
}

export interface Choice extends BaseEntity {
  code: string;
  textId: string;
}

export const NONE_CHOICE_CODE = 'NONE';
export const IDK_CHOICE_CODE = 'IDK';
export const YES_CHOICE_CODE = 'YES';
export const NO_CHOICE_CODE = 'NO';

export type FixedChoices = {
  idk: Choice;
  none: Choice;
};

interface QuestionBase extends BaseEntity {
  code: string;
  type: QuestionType;
  category?: string;
  image: Medium;
  titleId: string;
  subtitleId?: string;
  optional: boolean;
  last?: boolean;
  groupName?: string;
  orderIndex: number;
  explanatoryTextId?: string;
}

export interface BiChoiceQuestion extends QuestionBase {
  type: QuestionType.BI_CHOICE;
  leftChoice: Choice;
  rightChoice: Choice;
  idkOption: boolean;
}

export interface ChoiceQuestion extends QuestionBase {
  type: QuestionType.CHOICE;
  multiple: boolean;
  allowNone: boolean;
  primaryChoices: Choice[];
  secondaryChoices?: Choice[];
  noneChoiceTextId?: string;
}

export interface AutocompleteQuestion extends QuestionBase {
  type: QuestionType.AUTOCOMPLETE;
  multiple: boolean;
  placeholderId?: string;
}

export interface AutocompleteQuestionOption {
  id: string | number;
  code: string;
  name: string;
  group?: string;
}

export interface DateQuestion extends QuestionBase {
  type: QuestionType.DATE;
  placeholderId?: string;
  minOffsetDays?: number;
  maxOffsetDays?: number;
  approximate?: boolean;
}

export interface NumberQuestion extends QuestionBase {
  type: QuestionType.NUMBER;
  min: number;
  max: number;
  step: number;
  placeholderId?: string;
  defaultValue?: number;
  convertibleUnits?: ConvertibleUnit[];
  unitId?: string;
}

export interface RatingQuestion extends QuestionBase {
  type: QuestionType.RATING;
  minRating: number;
  maxRating: number;
}

export interface StatementQuestion extends QuestionBase {
  type: QuestionType.STATEMENT;
  buttonTextId?: string;
}

export interface TextQuestion extends QuestionBase {
  type: QuestionType.TEXT;
  placeholderId?: string;
  maxLength: number;
}

export interface UploadQuestion extends QuestionBase {
  type: QuestionType.UPLOAD;
  multiple: boolean;
  acceptedTypes: MediumType[];
  maxFileSizeMb: number;
  postponable?: boolean;
  manualEntryEligible?: boolean;
  manualEntryTextId?: string;
  skipManualEntryEligible?: boolean;
  skipManualEntryTextId?: string;
}

export type Question =
  | BiChoiceQuestion
  | ChoiceQuestion
  | AutocompleteQuestion
  | DateQuestion
  | NumberQuestion
  | RatingQuestion
  | StatementQuestion
  | TextQuestion
  | UploadQuestion;

export const isBiChoiceQuestion = (
  q?: Question | null
): q is BiChoiceQuestion => !!q && q.type === QuestionType.BI_CHOICE;

export const isYesNoChoice = (choice?: Choice | null): boolean =>
  choice?.code === YES_CHOICE_CODE || choice?.code === NO_CHOICE_CODE;

export const isYesNoQuestion = (q?: Question | null): boolean => {
  if (!isBiChoiceQuestion(q)) {
    return false;
  }

  return [q.leftChoice, q.rightChoice].every(isYesNoChoice);
};

export const isChoiceQuestion = (q?: Question | null): q is ChoiceQuestion =>
  !!q && q.type === QuestionType.CHOICE;

export const isAutocompleteQuestion = (
  q?: Question | null
): q is AutocompleteQuestion => !!q && q.type === QuestionType.AUTOCOMPLETE;

export const isDateQuestion = (q?: Question | null): q is DateQuestion =>
  !!q && q.type === QuestionType.DATE;

export const isNumberQuestion = (q?: Question | null): q is NumberQuestion =>
  !!q && q.type === QuestionType.NUMBER;

export const isRatingQuestion = (q?: Question | null): q is RatingQuestion =>
  !!q && q.type === QuestionType.RATING;

export const isStatementQuestion = (
  q?: Question | null
): q is StatementQuestion => !!q && q.type === QuestionType.STATEMENT;

export const isTextQuestion = (q?: Question | null): q is TextQuestion =>
  !!q && q.type === QuestionType.TEXT;

export const isUploadQuestion = (q?: Question | null): q is UploadQuestion =>
  !!q && q.type === QuestionType.UPLOAD;
